import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Target, Lightbulb, Heart, ArrowRight, Calendar, MapPin, Users } from 'lucide-react';
import { MILESTONES } from '../data/mock';
import aboutHeroImg from '../assets/images/about_hero.jpg';

const values = [
  {
    icon: Award,
    title: "Excellence",
    description: "We strive for perfection in every detail, from booth design to customer experience."
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Constantly pushing boundaries with latest technology and creative solutions."
  },
  {
    icon: Heart,
    title: "Passion",
    description: "Our love for creating memorable experiences drives everything we do."
  },
  {
    icon: Target,
    title: "Reliability",
    description: "13+ years of consistent quality and professional service delivery."
  }
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Our Story
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mt-4 mb-8">
              Chennai&apos;s First,{' '}
              <span className="gold-gradient-text">India&apos;s Leading</span>
            </h1>
            <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
              Born in 2012, Inkfly Photo Booth started with a simple vision - to transform ordinary events
              into extraordinary memories. Today, we&apos;re proud to be one of India&apos;s most trusted photo booth
              companies, having served over 5000 events across the nation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden aspect-[21/9]"
          >
            <img
              src={aboutHeroImg}
              alt="Inkfly Photo Booth Event"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="font-display text-6xl md:text-8xl lg:text-9xl font-bold text-white">
                  13+
                </p>
                <p className="text-white/80 text-lg md:text-xl uppercase tracking-widest mt-2">
                  Years of Excellence
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                From a Dream to{' '}
                <span className="gold-gradient-text">Industry Leadership</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  In 2012, when photo booths were virtually unheard of in South India, we took a leap of
                  faith. Armed with passion and a single photo booth, we set out to create experiences
                  that would make people smile.
                </p>
                <p>
                  Fast forward to today, and Inkfly has become synonymous with premium photo booth
                  experiences in India. We&apos;ve evolved from a small Chennai-based startup to a pan-India
                  brand trusted by top Corporates, Agencies, wedding planners and Event planners.
                </p>
                <p>
                  Our journey has been defined by continuous innovation - from introducing India&apos;s first
                  Time Freeze to pioneering AI-powered photo experiences. We don&apos;t just follow trends; we create them.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: Calendar, label: "Founded", value: "2012" },
                { icon: MapPin, label: "Headquarters", value: "Chennai" },
                { icon: Users, label: "Events", value: "5000+" },
                { icon: Award, label: "Experience", value: "13+ Years" }
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-gray-50 border border-gray-100 text-center"
                >
                  <item.icon className="w-8 h-8 text-[#00BFFF] mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-1">{item.label}</p>
                  <p className="font-display text-2xl font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              Our Journey
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              Milestones That Define Us
            </h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gradient-to-b from-[#00BFFF] via-[#00BFFF]/50 to-transparent hidden md:block" />

            <div className="space-y-12">
              {MILESTONES.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm inline-block">
                      <p className="font-display text-3xl font-bold gold-gradient-text mb-2">
                        {milestone.year}
                      </p>
                      <h3 className="text-gray-900 font-semibold text-lg mb-1">{milestone.title}</h3>
                      <p className="text-gray-600 text-sm">{milestone.desc}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-[#00BFFF] border-4 border-gray-50 z-10 hidden md:block" />
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00BFFF] text-sm uppercase tracking-widest font-medium">
              What Drives Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-xl bg-gray-50 border border-gray-100 hover:border-[#00BFFF]/30 hover:shadow-lg transition-all text-center group"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#00BFFF]/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <value.icon className="w-8 h-8 text-[#00BFFF]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Be Part of Our Story?
            </h2>
            <p className="text-gray-600 mb-8">
              Let&apos;s create something amazing together. Whether you&apos;re planning an event or looking
              to start your own photo booth business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="btn-secondary">
                Explore Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
